import type { FAQ } from "../../types";

export const gitlabFaqs: FAQ[] = [
  {
    id: "faq-1",
    question: "How does Null Autos handle AAOS emulator behavioral parity with real hardware?",
    answer:
      "Our emulators run the full AAOS build using Google's Cuttlefish reference device. Multi-display and multi-user are fully supported. The Vehicle HAL (VHAL) covers most properties, though some hardware-specific ones require stubs. We clearly document what works in emulation vs. what requires HIL validation.",
    category: "Architecture",
    tags: ["emulator", "parity", "VHAL", "cuttlefish"],
  },
  {
    id: "faq-2",
    question: "What Arm architectures are supported?",
    answer:
      "Today we run Arm-based emulators on AWS G5g instances (ARMv8.2-A with GPU acceleration). As AWS releases Graviton4/5 GPU instances, we'll support ARMv9.0-A. Full ARMv9.2-A parity requires physical silicon — no cloud provider offers this yet. We're transparent about this path and can work together on a hardware integration strategy.",
    category: "Architecture",
    tags: ["arm", "graviton", "aws", "architecture"],
  },
  {
    id: "faq-3",
    question: "How does authentication work?",
    answer:
      "We use standard OIDC (OpenID Connect) for web portal access and a CLI-based OIDC flow for ADB connections via `nullctl`. We integrate with your existing identity provider — no separate credentials needed. All connections are encrypted and access-controlled.",
    category: "Security",
    tags: ["auth", "oidc", "security", "adb"],
  },
  {
    id: "faq-4",
    question: "Can we run compliance tests (CTS/VTS) in emulators?",
    answer:
      "Yes — CTS, VTS, Treble, and Trout test suites can run in emulators for shift-left testing. This catches issues early in the development cycle. Full compliance certification still requires hardware, but running these tests in emulators dramatically reduces the feedback loop from days to hours.",
    category: "Compliance",
    tags: ["cts", "vts", "treble", "trout", "compliance", "testing"],
  },
  {
    id: "faq-5",
    question: "What about QNX host OS support?",
    answer:
      "Yocto Linux builds are fully supported and demo-ready today. QNX support requires a partnership discussion due to the proprietary toolchain. We can discuss the path forward for QNX integration during the onsite.",
    category: "Architecture",
    tags: ["qnx", "yocto", "linux", "host-os"],
  },
  {
    id: "faq-6",
    question: "How does the software factory integrate with GitLab?",
    answer:
      "Our platform is GitLab-native. We provide custom CI/CD runners optimized for AAOS builds, snapshot-based caching for faster builds, and automated test pipelines. Developers use their familiar GitLab workflow — push code, CI triggers builds, tests run automatically on cloud emulators.",
    category: "Integration",
    tags: ["gitlab", "ci-cd", "runners", "software-factory"],
  },
  {
    id: "faq-7",
    question: "What does developer onboarding look like?",
    answer:
      "Cloud workstations are pre-configured per persona (Platform Dev, App Dev, Mobile Dev) using devfiles. A new developer can be productive in minutes, not days. No local machine setup required — everything runs in the cloud with full IDE support.",
    category: "Developer Experience",
    tags: ["onboarding", "workstations", "devfile", "developer-experience"],
  },
  {
    id: "faq-8",
    question: "How is infrastructure managed?",
    answer:
      "Null Autos is a managed platform. We handle all infrastructure provisioning, scaling, and maintenance. No IaC authoring required from your team. The platform is Kubernetes-native, auto-scaling, and multi-region capable.",
    category: "Infrastructure",
    tags: ["infrastructure", "managed", "kubernetes", "scaling"],
  },
  {
    id: "faq-9",
    question: "What is the deployment model?",
    answer:
      "We offer cloud-hosted (our infrastructure), cloud-dedicated (your cloud account, our management), and hybrid options. The platform can run in AWS, GCP, or Azure. For sensitive workloads, we support private connectivity and dedicated tenancy.",
    category: "Infrastructure",
    tags: ["deployment", "cloud", "hybrid", "multi-cloud"],
  },
  {
    id: "faq-10",
    question: "How does the HIL (Hardware-in-the-Loop) integration work?",
    answer:
      "Validated images from our cloud pipeline can deploy directly to HIL rigs, closing the loop from code to silicon. The control plane manages the connection between cloud-built images and physical hardware, enabling a seamless transition from virtual to physical validation.",
    category: "Architecture",
    tags: ["hil", "hardware", "silicon", "validation"],
  },
];
